import React from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, Star, ArrowRight } from "lucide-react";
import { api } from "../lib/api";

/**
 * HeroReviewsWidget — compact social-proof strip showing average rating + latest reviews.
 * Designed for placement in the dark hero section. Trustpilot-inspired green badge.
 *
 * Strategy: fetch /api/reviews/public on mount, render star rating + count + 2-3 review snippets.
 */
export default function HeroReviewsWidget() {
  const [data, setData] = React.useState(null);

  React.useEffect(() => {
    api.get("/reviews/public", { params: { limit: 3 } })
      .then(({ data }) => setData(data))
      .catch(() => setData({ stats: { count: 0, average: 0 }, items: [] }));
  }, []);

  if (!data) return null;
  const { stats, items } = data;
  if (!stats?.count) return null;

  return (
    <div className="bg-white/5 border border-white/10 backdrop-blur-md p-4 sm:p-5 max-w-xl" data-testid="hero-reviews-widget">
      <div className="flex items-center gap-3">
        <div className="flex items-center gap-1.5">
          {Array.from({ length: 5 }).map((_, i) => (
            <Star
              key={i}
              size={16}
              className={i < Math.round(stats.average) ? "fill-[#00B67A] text-[#00B67A]" : "text-white/30"}
            />
          ))}
        </div>
        <div className="text-white text-sm">
          <span className="font-bold text-base">{stats.average.toFixed(1)}</span>
          <span className="text-white/60"> · {stats.count} verificerede anmeldelser</span>
        </div>
        <ShieldCheck size={14} className="text-[#00B67A] ml-auto" />
      </div>
      {items?.length > 0 && (
        <div className="mt-3 flex gap-3 overflow-x-auto pb-1 -mx-1 px-1">
          {items.slice(0, 3).map((r) => (
            <div key={r.id} className="flex-shrink-0 w-56 text-xs">
              <div className="flex items-center gap-1">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Star key={i} size={10} className={i < r.rating ? "fill-[#00B67A] text-[#00B67A]" : "text-white/20"} />
                ))}
              </div>
              <div className="text-white/90 font-semibold mt-1 truncate">{r.title}</div>
              <div className="text-white/50 mt-0.5 line-clamp-2">{r.body}</div>
              <div className="text-white/40 mt-1 text-[10px] uppercase tracking-wider">— {r.display_name}</div>
            </div>
          ))}
        </div>
      )}
      <Link
        to="/anmeldelser"
        className="text-white/80 hover:text-white text-xs font-semibold tracking-wider uppercase inline-flex items-center gap-1 mt-3"
        data-testid="hero-reviews-link"
      >
        Læs alle anmeldelser <ArrowRight size={12} />
      </Link>
    </div>
  );
}
